import { useQuery } from '@tanstack/react-query';
import { add } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import runtimeEnv from '~/shared/utils/runtimeEnv';
import { SelectOption, Tenants } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import { getDateFilterDates } from '~/shared/utils/getDateFilterDates';

import { getApiFilterFromSelectOption } from '../FleetManagerPage/helpers';

import { dateFilterItems } from './dateFilterMenuItems';
import { datafyDevelopmentTenantId, getFilterSearchParams } from './helpers';
import { utilizationFullGraphKey } from './serachParamKeys';
import { refreshStateMenuItems } from './refreshStateMenuItems';

export default function useUtilizationQueryParams({
  utilizationDateFilterSearchParam,
  showFullGraphSearchParam,
  apiFiltersSearchParam,
  volumeId,
  refreshStateSearchParams,
}: {
  utilizationDateFilterSearchParam: string | null;
  showFullGraphSearchParam: string | null;
  apiFiltersSearchParam?: string | null;
  refreshStateSearchParams?: string | null;
  volumeId?: string;
}) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const api = useXFetch();

  const refetchInterval = refreshStateMenuItems.find(
    (item) => item.title === refreshStateSearchParams
  )?.time;

  const { data: activeTenantId } = useQuery({
    queryKey: ['getActiveTenantId'],
    queryFn: async () => {
      const tenants = await api<Tenants>(
        `${runtimeEnv.fronteggBaseUrl}/frontegg/identity/resources/users/v3/me/tenants`
      );
      return tenants.activeTenant.tenantId;
    },
    refetchOnWindowFocus: false,
  });

  function onRefetchUtilizationQuery() {
    setCurrentDate(new Date());
  }

  const showFullGraph =
    !!showFullGraphSearchParam ||
    (activeTenantId && datafyDevelopmentTenantId.includes(activeTenantId));

  const activeDateFilterDuration = dateFilterItems.find(
    (f) => f.title === utilizationDateFilterSearchParam
  )?.duration;

  const [startDate, endDate] = useMemo(() => {
    const dateRange = getDateFilterDates(
      activeDateFilterDuration,
      utilizationDateFilterSearchParam,
      currentDate
    );
    return dateRange;
  }, [activeDateFilterDuration, currentDate, utilizationDateFilterSearchParam]);

  const parsedFiltersSearchParams: string[] = (
    JSON.parse(apiFiltersSearchParam || '[]') as SelectOption[]
  )?.map((f: SelectOption) => getApiFilterFromSelectOption(f));

  const utilizationQueryUrl = useMemo(() => {
    const utilizationQueryParams = [
      ...(apiFiltersSearchParam ? parsedFiltersSearchParams.map((f) => ['filters', f]) : []),
      ['start_date', startDate],
      ['end_date', endDate],
      ...(showFullGraph ? [[utilizationFullGraphKey, 'true']] : []),
      ...(volumeId
        ? getFilterSearchParams([{ field: 'volumeId', value: volumeId!, op: 'equals' }])
        : []),
    ];

    const utilizationQs = new URLSearchParams(utilizationQueryParams);

    return `/api/v1/volumes/utilization-graph?${utilizationQs.toString()}`;
  }, [
    apiFiltersSearchParam,
    endDate,
    showFullGraph,
    startDate,
    volumeId,
    parsedFiltersSearchParams,
  ]);

  useEffect(() => {
    if (refetchInterval) {
      const intervalId = setInterval(() => {
        const newDate = add(new Date(), { seconds: refetchInterval / 1000 });
        setCurrentDate(newDate);
      }, refetchInterval);

      return () => clearInterval(intervalId);
    }
  }, [refetchInterval, startDate]);

  return { utilizationQueryUrl, onRefetchUtilizationQuery };
}
